import { QuizCompetitionSettings } from "../components/QuizCompetitionSettings";
import { RulesTranslationStatus } from "../components/RulesTranslationStatus";
import {AiRoutingSettings} from "../components/AiRoutingSettings";
import { useEffect, useState, type FormEvent } from "react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import type { CatalogItem, CatalogResource, Catalogs, Event, ReviewCriterion } from "../types";
import { api } from "../api";
import { Modal } from "../components/Modal";
import { FloorPlanManager } from "../components/FloorPlanManager";
import { ActionIcon, AdminActionButton, DeleteIcon, EditIcon, IconActionButton, IconActionLink } from "../components/AdminActions";
import { confirmDelete, deleteFailed } from "../utils/confirmDelete";

type Props = {
  event: Event;
  catalogs: Catalogs;
  saving: boolean;
  superAdmin?: boolean;
  onSave: (event: FormEvent<HTMLFormElement>) => void;
  onAdd: (resource: CatalogResource) => void;
  onEdit: (resource: CatalogResource, item: CatalogItem) => void;
  onDelete: (resource: CatalogResource, item: CatalogItem) => void;
  onEventRefresh: () => Promise<void>;
};

type OpenAiUsageReport = {
  currency: string;
  queriedAt: string;
  totals: { last7Days: number; last30Days: number; currentMonth: number };
  projects: Array<{ id: string; name: string; status: string; amount: number }>;
  details: Array<{
    lineItem: string;
    projectId: string;
    projectName: string;
    amount: number;
  }>;
  daily: Array<{ date: string; amount: number }>;
  balance: number | null;
  balanceMessage: string;
  spendLimit: null | {
    thresholdAmount: number;
    currency: string;
    interval: string;
    enforcement: string;
    usedAmount: number | null;
    remainingAmount: number | null;
  };
  localUsage: {
    periodDays: number;
    transactionCount: number;
    shownTransactions: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    estimatedUsd: number;
    transactions: Array<{
      id: number;
      scope: string;
      feature: string;
      description: string;
      model: string;
      inputTokens: number;
      outputTokens: number;
      totalTokens: number;
      estimatedUsd: number;
      createdAt: string;
    }>;
  };
  credentials?: OpenAiKeyStatus;
};

type OpenAiKeyStatus = {
  apiKeySource: "event" | "environment" | "missing";
  apiKeyFingerprint: string;
  adminKeySource: "event" | "environment" | "missing";
  adminKeyConfigured: boolean;
  adminKeyFingerprint: string;
  projectId: string;
  model: string;
  providerProjectId?: string;
};

function credentialSourceLabel(source: "event" | "environment" | "missing") {
  if (source === "event") return "Guardada cifrada en este evento";
  if (source === "environment") return "Tomada del archivo .env del servidor";
  return "Sin configurar";
}

function AiScopeControl({
  code,
  title,
  help,
  enabled,
  onEnabledChange,
  mode: initialMode,
  limit,
  spent,
}: {
  code: string;
  title: string;
  help: string;
  enabled: number;
  onEnabledChange: (enabled: boolean) => void;
  mode: "free" | "capped";
  limit: number;
  spent: number;
}) {
  const [mode, setMode] = useState(initialMode || "free");
  const [creditLimit, setCreditLimit] = useState(String(limit || ""));
  const remaining = Math.max(0, Number(creditLimit || 0) - Number(spent || 0));
  return (
    <article className="ai-scope-control">
      <input type="hidden" name={`ai${code}Mode`} value={mode} />
      <input type="hidden" name={`ai${code}LimitUsd`} value={creditLimit} />
      <div className="ai-scope-chain">
        <label className="ai-module-toggle">
          <input
            name={`ai${code}Enabled`}
            value="1"
            type="checkbox"
            role="switch"
            checked={Boolean(enabled)}
            onChange={(event) => onEnabledChange(event.target.checked)}
          />
          <span className="ai-toggle-track" aria-hidden="true" />
          <span><strong>{title}</strong><small>{help}</small></span>
        </label>
        {Boolean(enabled) && <>
          <label className="field ai-scope-mode">
            <span>Modalidad</span>
            <select value={mode} onChange={(event) => setMode(event.target.value as "free" | "capped")}>
              <option value="free">Libre</option>
              <option value="capped">Tope de crédito en USD</option>
            </select>
          </label>
          {mode === "capped" && <label className="field ai-scope-limit">
            <span>Límite de crédito (USD)</span>
            <input type="number" min="0.01" step="0.01" value={creditLimit}
              onChange={(event) => setCreditLimit(event.target.value)} required />
          </label>}
        </>}
      </div>
      {Boolean(enabled) && <p className="field-help">
        {mode === "capped"
          ? `Consumido: USD ${Number(spent || 0).toFixed(4)} · Disponible: USD ${remaining.toFixed(4)}`
          : `Consumo estimado acumulado: USD ${Number(spent || 0).toFixed(4)}`}
      </p>}
    </article>
  );
}

type SubmissionModality = NonNullable<Event["submissionModalities"]>[number];

const defaultSubmissionModalities: SubmissionModality[] = [
  { code: "abstract", title: "Abstract", enabled: true, maxCoauthors: 0, specialOptions: [] },
  { code: "poster", title: "ePoster", enabled: true, maxCoauthors: 0, specialOptions: [] },
  { code: "clinical_case", title: "Caso clínico", enabled: true, maxCoauthors: 0, specialOptions: [] },
];

function submissionModalitiesForEvent(event: Event) {
  return defaultSubmissionModalities.map((base) => {
    const saved = event.submissionModalities?.find((item) => item.code === base.code);
    return saved ? { ...base, ...saved, specialOptions: [...(saved.specialOptions || [])] } : { ...base, specialOptions: [] };
  });
}

function SubmissionModalitiesControl({ event }: { event: Event }) {
  const [items, setItems] = useState<SubmissionModality[]>(() => submissionModalitiesForEvent(event));
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    setItems(submissionModalitiesForEvent(event));
    setDrafts({});
  }, [event.id]);

  const update = (code: SubmissionModality["code"], change: Partial<SubmissionModality>) =>
    setItems((current) => current.map((item) => item.code === code ? { ...item, ...change } : item));

  const addSpecial = (item: SubmissionModality) => {
    const title = (drafts[item.code] || "").trim();
    if (!title || item.specialOptions.some((option) => option.toLocaleLowerCase() === title.toLocaleLowerCase())) return;
    update(item.code, { specialOptions: [...item.specialOptions, title] });
    setDrafts((current) => ({ ...current, [item.code]: "" }));
  };

  return (
    <section className="submission-modalities-admin field-wide">
      <input type="hidden" name="submissionModalitiesJson" value={JSON.stringify(items)} />
      <header>
        <div>
          <span className="eyebrow">Modalidades admitidas</span>
          <h3>Formatos habilitados en Abstracts</h3>
          <p>Solo las modalidades activas podrán seleccionarse al crear un trabajo.</p>
        </div>
        <strong>{items.filter((item) => item.enabled).length} activas</strong>
      </header>
      <div className="submission-special-heading">
        <strong>Aplicaciones para casos especiales</strong>
        <small>Escribí los títulos que aparecerán como opciones dentro de cada modalidad.</small>
      </div>
      <div className="submission-modality-list">
        {items.map((item) => <article className={`submission-modality-card ${item.enabled ? "enabled" : ""}`} key={item.code}>
          <label className="submission-modality-toggle">
            <input type="checkbox" checked={item.enabled} onChange={(change) => update(item.code, { enabled: change.target.checked })} />
            <span aria-hidden="true" />
            <strong>{item.title}</strong>
          </label>
          <label className="field submission-coauthors-limit">
            <span>Máximo de coautores</span>
            <input
              type="number"
              min="0"
              max="500"
              step="1"
              value={item.maxCoauthors}
              onChange={(change) => update(item.code, { maxCoauthors: Math.max(0, Math.min(500, Number(change.target.value) || 0)) })}
            />
            <small>Además del autor principal. 0 significa sin límite.</small>
          </label>
          {item.code === "clinical_case" && <div className="clinical-introduction-settings">
            <label className="check-field"><input type="checkbox" checked={Boolean(item.introductionEnabled)} onChange={(change) => update(item.code, { introductionEnabled: change.target.checked })} /><span>Habilitar introducción del caso clínico</span></label>
            {item.introductionEnabled && <label className="field"><span>Máximo de caracteres de la introducción</span><input type="number" min="1" max="100000" required value={item.introductionMaxChars ?? 3000} onChange={(change) => update(item.code, { introductionMaxChars: Math.max(1, Math.min(100000, Number(change.target.value) || 1)) })} /><small>Incluye espacios. Se muestra en la pestaña de contenido del caso clínico.</small></label>}
          </div>}
          <div className="submission-special-options">
            {item.specialOptions.map((option) => <div className="submission-special-row" key={option}>
              <span>{option}</span>
              <button type="button" aria-label={`Quitar ${option}`} onClick={() => update(item.code, { specialOptions: item.specialOptions.filter((value) => value !== option) })}>×</button>
            </div>)}
            <div className="submission-special-add">
              <input value={drafts[item.code] || ""} maxLength={120} placeholder="Ej.: Premio joven investigador" onChange={(change) => setDrafts((current) => ({ ...current, [item.code]: change.target.value }))} onKeyDown={(key) => { if (key.key === "Enter") { key.preventDefault(); addSpecial(item); } }} />
              <button type="button" aria-label={`Agregar caso especial para ${item.title}`} title="Agregar" onClick={() => addSpecial(item)}>+</button>
            </div>
          </div>
        </article>)}
      </div>
    </section>
  );
}

const masters: Array<{
  resource: CatalogResource;
  code: string;
  title: string;
  description: string;
}> = [
  {
    resource: "days",
    code: "DÍ",
    title: "Días del evento",
    description: "Jornadas y orden del programa",
  },
  {
    resource: "blocks",
    code: "BH",
    title: "Bloques horarios",
    description: "Franjas configuradas para cada jornada",
  },
  {
    resource: "rooms",
    code: "SA",
    title: "Salas",
    description: "Espacios físicos y transmisiones",
  },
  {
    resource: "topics",
    code: "TE",
    title: "Temáticas",
    description: "Áreas del programa científico",
  },
  {
    resource: "modes",
    code: "MO",
    title: "Modalidades",
    description: "Conferencias, mesas y talleres",
  },
  {
    resource: "roles",
    code: "RO",
    title: "Roles",
    description: "Funciones de cada participante",
  },
  {
    resource: "facultyTypes",
    code: "TF",
    title: "Tipos de Faculty",
    description:
      "Nacionales, latinoamericanos, internacionales, industria y otras categorías",
  },
  {
    resource: "eventStatuses",
    code: "EE",
    title: "Estados del evento",
    description:
      "Etapas operativas que el Organigrama puede aplicar al procesar un hito",
  },
  {
    resource: "scientificTables",
    code: "MC",
    title: "Mesas científicas",
    description: "Mesas de orales y sesiones de pósters para asignar trabajos",
  },
  {
    resource: "exhibitors",
    code: "EX",
    title: "Expositores",
    description: "Empresas y stands comerciales",
  },
];

export function SettingsPage({
  event,
  catalogs,
  saving,
  superAdmin,
  onSave,
  onAdd,
  onEdit,
  onDelete,
  onEventRefresh,
}: Props) {
  const [aiModulesEnabled, setAiModulesEnabled] = useState<Record<string, number>>({
    organize: event.aiOrganizeEnabled,
    abstracts: event.aiJuryEnabled,
    meetingplanner: event.aiMeetingPlannerEnabled,
  });
  useEffect(() => {
    setAiModulesEnabled({ organize: event.aiOrganizeEnabled, abstracts: event.aiJuryEnabled, meetingplanner: event.aiMeetingPlannerEnabled });
  }, [event.id, event.aiOrganizeEnabled, event.aiJuryEnabled, event.aiMeetingPlannerEnabled]);
  const [fontScalePercent, setFontScalePercent] = useState(
    Math.max(85, Math.min(140, Number(event.fontScalePercent || 100))),
  );
  const [sqlServer, setSqlServer] = useState<{
    enabled: boolean;
    status: string;
    pending: number;
    processed: number;
    failed: number;
    lastSuccessAt: string | null;
    lastError: string | null;
  } | null>(null);
  const [reviewWorkflowBusy, setReviewWorkflowBusy] = useState(false);
  const [reviewWorkflowMessage, setReviewWorkflowMessage] = useState("");
  const [reviewCriteria, setReviewCriteria] = useState<ReviewCriterion[]>(event.reviewCriteria || []);
  const [classificationMetric, setClassificationMetric] = useState(event.classificationMetric || "average");
  const [templates, setTemplates] = useState<
    Array<{
      id: number;
      name: string;
      workType: string;
      fileName: string;
      size: number;
      downloadUrl: string;
    }>
  >([]);
  const [templateName, setTemplateName] = useState("");
  const [templateWorkType, setTemplateWorkType] = useState("all");
  const [templateBusy, setTemplateBusy] = useState(false);
  const [openAiUsage, setOpenAiUsage] = useState<OpenAiUsageReport | null>(
    null,
  );
  const [openAiUsageBusy, setOpenAiUsageBusy] = useState(false);
  const [openAiKeyCheckBusy, setOpenAiKeyCheckBusy] = useState(false);
  const [aiApiKeyDraft, setAiApiKeyDraft] = useState("");
  const [openAiAdminKeyDraft, setOpenAiAdminKeyDraft] = useState("");
  const [openAiProjectIdDraft, setOpenAiProjectIdDraft] = useState(
    event.openAiProjectId || "",
  );
  const [credentialStatus, setCredentialStatus] =
    useState<OpenAiKeyStatus | null>(null);
  const loadTemplates = () =>
    api<{ items: typeof templates }>(
      `/api/call-for-science/templates?eventId=${event.id}`,
    )
      .then(({ items }) => setTemplates(items))
      .catch(() => undefined);
  useEffect(() => {
    const load = () =>
      api<NonNullable<typeof sqlServer>>("/api/integrations/sqlserver/status")
        .then(setSqlServer)
        .catch(() => undefined);
    load();
    const timer = window.setInterval(load, 10000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    loadTemplates();
  }, [event.id]);
  useEffect(() => {
    setOpenAiProjectIdDraft(event.openAiProjectId || "");
    setCredentialStatus(null);
    setAiApiKeyDraft("");
    setOpenAiAdminKeyDraft("");
  }, [event.id, event.openAiProjectId]);
  useEffect(() => {
    setReviewCriteria(event.reviewCriteria || []);
    setClassificationMetric(event.classificationMetric || "average");
  }, [event.id, event.reviewCriteria, event.classificationMetric]);
  useEffect(() => {
    const saved = Math.max(
      85,
      Math.min(140, Number(event.fontScalePercent || 100)),
    );
    setFontScalePercent(saved);
  }, [event.id, event.fontScalePercent]);
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--site-font-scale",
      String(fontScalePercent / 100),
    );
    return () =>
      document.documentElement.style.setProperty(
        "--site-font-scale",
        String(
          Math.max(85, Math.min(140, Number(event.fontScalePercent || 100))) /
            100,
        ),
      );
  }, [fontScalePercent, event.fontScalePercent]);
  async function runReviewWorkflow(action: "assign" | "start") {
    setReviewWorkflowBusy(true);
    setReviewWorkflowMessage("");
    try {
      const result = await api<any>(`/api/review-workflow/${action}`, {
        method: "POST",
        body: JSON.stringify({
          eventId: event.id,
          renotify: action === "start",
        }),
      });
      if (action === "assign")
        setReviewWorkflowMessage(
          `Asignación terminada: ${result.assigned} nuevas asignaciones${result.missing ? ` · faltan ${result.missing} cupos por cubrir` : ""}.`,
        );
      else
        setReviewWorkflowMessage(
          `Arbitraje iniciado: ${result.notification?.notified || 0} árbitros notificados${result.ai?.completed ? ` · ${result.ai.completed} evaluaciones por IA` : ""}${result.ai?.errors?.length ? ` · ${result.ai.errors.length} avisos de IA` : ""}.`,
        );
    } catch (error) {
      if ((error as {status?: number})?.status === 499) return ;
      setReviewWorkflowMessage(
        error instanceof Error
          ? error.message
          : "No se pudo ejecutar el flujo de arbitrajes",
      );
    } finally {
      setReviewWorkflowBusy(false);
    }
  }
  async function uploadTemplate(file?: File) {
    if (!file) return;
    setTemplateBusy(true);
    try {
      const data = await fileToDataUrl(file);
      await api("/api/call-for-science/templates", {
        method: "POST",
        body: JSON.stringify({
          eventId: event.id,
          name: templateName || file.name,
          workType: templateWorkType,
          fileName: file.name,
          data,
        }),
      });
      setTemplateName("");
      await loadTemplates();
    } finally {
      setTemplateBusy(false);
    }
  }
  async function deleteTemplate(id: number) {
    const template = templates.find((item) => item.id === id);
    if (!await confirmDelete(template?.name || "Plantilla", "Se eliminará la plantilla publicada. Esta acción no se puede deshacer.")) return;
    try {
      await api(`/api/call-for-science/templates/${id}`, { method: "DELETE" });
      await loadTemplates();
    } catch (error) {
      await deleteFailed(error, "No se pudo eliminar la plantilla");
    }
  }
  async function queryOpenAiUsage() {
    setOpenAiUsageBusy(true);
    try {
      setOpenAiUsage(
        await api<OpenAiUsageReport>(`/api/events/${event.id}/openai-usage`),
      );
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "No se pudo consultar OpenAI",
        text:
          error instanceof Error
            ? error.message
            : "La consulta de consumo no pudo completarse",
        confirmButtonText: "Entendido",
        confirmButtonColor: event.primaryColor || "#169c98",
      });
    } finally {
      setOpenAiUsageBusy(false);
    }
  }
  async function saveOpenAiCredentials() {
    const projectId = openAiProjectIdDraft.trim();
    if (!/^proj_[A-Za-z0-9]+$/.test(projectId)) {
      await Swal.fire({
        icon: "warning",
        title: "Falta el Project ID",
        text: "Ingresá la identificación completa del proyecto de OpenAI, comenzando con proj_.",
        confirmButtonText: "Entendido",
        confirmButtonColor: event.primaryColor || "#169c98",
      });
      return;
    }
    setOpenAiKeyCheckBusy(true);
    try {
      const status = await api<OpenAiKeyStatus>(
        `/api/events/${event.id}/openai-credentials`,
        {
          method: "POST",
          body: JSON.stringify({
            aiApiKey: aiApiKeyDraft,
            openAiAdminKey: openAiAdminKeyDraft,
            openAiProjectId: projectId,
          }),
        },
      );
      setCredentialStatus(status);
      setAiApiKeyDraft("");
      setOpenAiAdminKeyDraft("");
      await onEventRefresh();
      await Swal.fire({
        icon: "success",
        title: "Credenciales guardadas",
        text: `API Key: ${credentialSourceLabel(status.apiKeySource)}\nAdmin Key: ${credentialSourceLabel(status.adminKeySource)}\nProyecto: ${status.projectId}`,
        confirmButtonText: "Entendido",
        confirmButtonColor: event.primaryColor || "#169c98",
      });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "No se guardaron las credenciales",
        text:
          error instanceof Error
            ? error.message
            : "No se pudo guardar la configuración de OpenAI",
        confirmButtonText: "Entendido",
        confirmButtonColor: event.primaryColor || "#169c98",
      });
    } finally {
      setOpenAiKeyCheckBusy(false);
    }
  }
  async function verifyOpenAiKey() {
    setOpenAiKeyCheckBusy(true);
    try {
      const status = await api<OpenAiKeyStatus>(
        `/api/events/${event.id}/openai-key-status`,
        { method: "POST" },
      );
      if (status.providerProjectId)
        setOpenAiProjectIdDraft(status.providerProjectId);
      await onEventRefresh();
      const source = credentialSourceLabel(status.apiKeySource);
      const project =
        status.providerProjectId ||
        status.projectId ||
        "no informado por OpenAI";
      await Swal.fire({
        icon: "success",
        title: "La clave efectiva funciona",
        text: `Fuente: ${source}\nHuella: ${status.apiKeyFingerprint}\nModelo: ${status.model}\nProyecto: ${project}`,
        confirmButtonText: "Entendido",
        confirmButtonColor: event.primaryColor || "#169c98",
      });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "La clave efectiva no pudo responder",
        text:
          error instanceof Error
            ? error.message
            : "No se pudo verificar la clave",
        confirmButtonText: "Entendido",
        confirmButtonColor: event.primaryColor || "#169c98",
      });
    } finally {
      setOpenAiKeyCheckBusy(false);
    }
  }
  const openAiCredentials = (
          <div className="ai-superadmin-credentials">
            <div className="form-grid settings-form-grid ai-key-grid">
              <label className="field field-wide">
                <span>OpenAI API Key del evento</span>
                <input
                  name="aiApiKey"
                  type="password"
                  autoComplete="new-password"
                  value={aiApiKeyDraft}
                  onChange={(input) => setAiApiKeyDraft(input.target.value)}
                  placeholder={
                    event.aiApiKeyConfigured
                      ? "Clave guardada; dejá vacío para conservarla"
                      : "sk-…"
                  }
                />
                <small>
                  Clave efectiva actual:{" "}
                  <strong>
                    {credentialSourceLabel(
                      credentialStatus?.apiKeySource || event.aiApiKeySource,
                    )}
                  </strong>
                  . Las funciones de este evento usan exclusivamente esta
                  clave; la variable OPENAI_API_KEY del servidor no se utiliza
                  como reemplazo.
                </small>
              </label>
              <label className="field field-wide">
                <span>OpenAI Admin API Key para consumo</span>
                <input
                  name="openAiAdminKey"
                  type="password"
                  autoComplete="new-password"
                  value={openAiAdminKeyDraft}
                  onChange={(input) =>
                    setOpenAiAdminKeyDraft(input.target.value)
                  }
                  placeholder={
                    event.openAiAdminKeyConfigured ||
                    credentialStatus?.adminKeyConfigured
                      ? "Admin key configurada; dejá vacío para conservarla"
                      : "sk-admin-…"
                  }
                />
                <small>
                  Fuente actual:{" "}
                  <strong>
                    {credentialSourceLabel(
                      credentialStatus?.adminKeySource ||
                        event.openAiAdminKeySource,
                    )}
                  </strong>
                  .
                </small>
              </label>
              <label className="field field-wide">
                <span>Identificación del proyecto de OpenAI</span>
                <input
                  name="openAiProjectId"
                  value={openAiProjectIdDraft}
                  onChange={(input) =>
                    setOpenAiProjectIdDraft(input.target.value)
                  }
                  placeholder="proj_…"
                  pattern="proj_[A-Za-z0-9]+"
                />
                <small>
                  Se guarda junto con las credenciales y se recupera desde la
                  base de datos.
                </small>
              </label>
            </div>
            <div className="openai-credential-actions">
              <IconActionButton
                label="Guardar credenciales"
                icon="save"
                tone="primary"
                busy={openAiKeyCheckBusy}
                disabled={openAiKeyCheckBusy}
                onClick={() => void saveOpenAiCredentials()}
              />
              <IconActionButton
                label="Verificar API Key efectiva"
                icon="check"
                busy={openAiKeyCheckBusy}
                disabled={openAiKeyCheckBusy}
                onClick={() => void verifyOpenAiKey()}
              />
            </div>
          </div>
  );
  const openAiBilling = (
        <div className="openai-usage-query">
          <div>
            <strong>Facturación oficial de OpenAI</strong>
            <span>
              Consulta las credenciales ya almacenadas para este evento; no
              modifica la configuración.
            </span>
          </div>
          <IconActionButton
            label="Consultar consumo de OpenAI"
            icon="report"
            busy={openAiUsageBusy}
            disabled={openAiUsageBusy}
            onClick={() => void queryOpenAiUsage()}
          />
        </div>
  );
  return (
    <form className="page-stack" onSubmit={onSave}>
      <header className="page-header">
        <div>
          <span className="eyebrow">Configuración</span>
          <h1>Ajustes del evento</h1>
          <p>Datos generales y estructura que utilizan todos los módulos.</p>
        </div>
        <div className="header-actions">
          <IconActionButton
            label="Guardar datos generales"
            icon="save"
            tone="primary"
            type="submit"
            busy={saving}
            disabled={saving}
          />
        </div>
      </header>

      <details className="panel settings-main settings-full section-collapse">
        <summary className="section-collapse-summary">
          <div>
            <span className="eyebrow">Información general</span>
            <h2>{event.name}</h2>
          </div>
          <span className="settings-event-id">Evento #{event.id}</span><span className="section-collapse-chevron" aria-hidden="true">⌄</span>
        </summary>
        <div className="section-collapse-content">
        <div className="form-grid settings-form-grid">
          <label className="field field-wide">
            <span>Nombre del evento</span>
            <input name="name" defaultValue={event.name} required />
          </label>
          <label className="field">
            <span>Nombre corto</span>
            <input name="shortName" defaultValue={event.shortName} required />
          </label>
          <label className="field">
            <span>Etapa general</span>
            <select name="status" defaultValue={event.status}>
              <option value="draft">Borrador</option>
              <option value="active">En curso</option>
              <option value="closed">Cerrado</option>
            </select>
            <small>
              Describe el ciclo de vida; la disponibilidad se configura por
              separado.
            </small>
          </label>
          <label className="field">
            <span>Estado actual de Organize</span>
            <select name="workflowStatusId" defaultValue={event.workflowStatusId || ""}>
              <option value="">Sin estado aplicado</option>
              {catalogs.eventStatuses.filter(status => status.scope === "organize" && (status.active || status.id === event.workflowStatusId)).map(status => <option key={status.id} value={status.id}>{status.name}</option>)}
            </select>
            <small>Flujo de organización y programa.</small>
          </label>
          {Boolean(event.abstractsEnabled) && <label className="field">
            <span>Estado actual de Abstracts</span>
            <select name="abstractsWorkflowStatusId" defaultValue={event.abstractsWorkflowStatusId || ""}>
              <option value="">Sin estado aplicado</option>
              {catalogs.eventStatuses.filter(status => status.scope === "abstracts" && (status.active || status.id === event.abstractsWorkflowStatusId)).map(status => <option key={status.id} value={status.id}>{status.name}</option>)}
            </select>
            <small>Flujo de trabajos científicos y arbitraje.</small>
          </label>}
          <label className="field">
            <span>Fecha de inicio</span>
            <input
              name="startDate"
              type="date"
              defaultValue={event.startDate}
              required
            />
          </label>
          <label className="field">
            <span>Fecha de cierre</span>
            <input
              name="endDate"
              type="date"
              defaultValue={event.endDate}
              required
            />
          </label>
          <label className="field">
            <span>Sede</span>
            <input name="venue" defaultValue={event.venue} />
          </label>
          <label className="field">
            <span>Ciudad</span>
            <input name="city" defaultValue={event.city} />
          </label>
          <label className="field">
            <span>Idiomas habilitados</span>
            <input
              name="languages"
              defaultValue={event.languages}
              placeholder="es,en,pt"
            />
          </label>
          <label className="field">
            <span>Color principal</span>
            <div className="color-field">
              <input
                name="primaryColor"
                type="color"
                defaultValue={event.primaryColor}
              />
              <input
                defaultValue={event.primaryColor}
                readOnly
                aria-label="Código de color"
              />
            </div>
          </label>
        </div>
        <div className="settings-switches">
          <label>
            <input
              name="organizeEnabled"
              value="1"
              type="checkbox"
              defaultChecked={Boolean(event.organizeEnabled)}
            />
            <span>
              <strong>Activo en Organize</strong>
              <small>
                Disponible para administrar y seleccionar en el panel
              </small>
            </span>
          </label>
          <label>
            <input
              name="mobileEnabled"
              value="1"
              type="checkbox"
              defaultChecked={Boolean(event.mobileEnabled)}
            />
            <span>
              <strong>Publicado en Mobile</strong>
              <small>Aparece en la app; pueden publicarse varios eventos</small>
            </span>
          </label>
<label className="registration-toggle"><input type="checkbox" name="mobileInternalChatEnabled" value="1" defaultChecked={Boolean(event.mobileInternalChatEnabled)} /><span><strong>Habilitar Blog</strong><small>Habilita el Blog del evento en la app para los perfiles registrados.</small></span></label>
<div className="registration-settings field-wide">
        <label className="registration-toggle">
          <input type="checkbox" name="mobileRegistrationRequired" value="1" defaultChecked={Boolean(event.mobileRegistrationRequired)} />
          <span><strong>Registro obligatorio en la app</strong><small>Exige validar email o WhatsApp antes de acceder al evento.</small></span>
        </label>
        <label className="field registration-message">
          <span>Mensaje de registro obligatorio</span>
          <textarea name="mobileRegistrationMessage" maxLength={5000} rows={3} defaultValue={event.mobileRegistrationMessage || ""} placeholder="Para continuar, registrate y validá tu email o WhatsApp." />
          <small>Se muestra en un modal con el botón CONTINUE.</small>
        </label>
      </div>
          <label>
            <input
              name="abstractsEnabled"
              value="1"
              type="checkbox"
              defaultChecked={Boolean(event.abstractsEnabled)}
            />
            <span>
              <strong>Call for Science</strong>
              <small>Recepción y evaluación de trabajos</small>
            </span>
          </label>
          <label>
            <input
              name="meetingLive"
              value="1"
              type="checkbox"
              defaultChecked={Boolean(event.meetingLive)}
            />
            <span>
              <strong>Meeting Live</strong>
              <small>Acceso en vivo a las sesiones</small>
            </span>
          </label>
        </div>
        </div>
      </details>

      <details className="panel settings-main settings-full section-collapse">
        <summary className="section-collapse-summary">
          <div>
            <span className="eyebrow">Identidad visual</span>
            <h2>Imágenes compartidas del evento</h2>
            <p>
              El ícono identifica los grupos de WhatsApp y el banner encabeza
              Organize y Abstracts.
            </p>
          </div>
          <span className="section-collapse-chevron" aria-hidden="true">⌄</span>
        </summary>
        <div className="section-collapse-content">
        <div className="event-assets-grid">
          <EventAssetField
            name="whatsappIcon"
            removeName="removeWhatsappIcon"
            value={event.whatsappIconData}
            title="Ícono de WhatsApp"
            help="Cuadrado · JPG, PNG o WebP · hasta 2 MB"
          />
          <EventAssetField
            name="eventBanner"
            removeName="removeEventBanner"
            value={event.bannerData}
            title="Banner del evento"
            help="Horizontal · JPG, PNG o WebP · hasta 5 MB"
            wide
          />
          <EventAssetField
            name="eventSplash"
            removeName="removeEventSplash"
            value={event.splashData}
            title="Splash de la app móvil"
            help="Vertical · JPG, PNG o WebP · hasta 5 MB"
          />
          <EventAssetField
            name="emailHeader"
            removeName="removeEmailHeader"
            value={event.emailHeaderData}
            title="Encabezado de emails"
            help="Horizontal · JPG, PNG o WebP · hasta 5 MB"
            wide
          />
        </div>
          <fieldset className="assignment-field field-wide shared-login-background">
            <legend>Fondo compartido del acceso</legend>
            <p>Esta imagen y este color se utilizan en Organize y Call4Science para este evento.</p>
            <label className="field"><span>Color de fondo alternativo</span><input name="callForScienceHeroBackground" type="color" defaultValue={event.callForScienceHeroBackground || "#153B5C"} /></label>
            <EventAssetField name="callForScienceHeroImageFile" removeName="removeCallForScienceHeroImage" value={event.callForScienceHeroImage} title="Imagen de fondo del acceso" help="JPG, PNG o WebP · hasta 5 MB · se adapta cubriendo toda la columna izquierda" wide />
          </fieldset>
        </div>
      </details>

      <details className="panel settings-main settings-full section-collapse">
        <summary className="section-collapse-summary"><span><span className="eyebrow">Organize</span><strong>Textos del acceso a Organize</strong></span><span className="section-collapse-chevron" aria-hidden="true">⌄</span></summary>
        <div className="section-collapse-content"><div className="form-grid settings-form-grid">
          <fieldset className="assignment-field field-wide">
            <legend>Portada izquierda del acceso a Organize</legend>
            <p>Textos propios de Organize para este evento. El fondo se configura en Identidad visual.</p>
            <div className="form-grid">
              <label className="field field-wide"><span>Texto superior de Organize</span><input name="organizeHeroEyebrow" maxLength={160} defaultValue={event.organizeHeroEyebrow} placeholder="Organización científica, simplificada" /></label>
              <label className="field field-wide"><span>Título principal de Organize</span><textarea name="organizeHeroTitle" rows={3} maxLength={300} defaultValue={event.organizeHeroTitle} placeholder="Plataforma integrada de gestión de eventos científicos" /></label>
              <label className="field field-wide"><span>Subtítulo de Organize</span><textarea name="organizeHeroLead" rows={3} maxLength={600} defaultValue={event.organizeHeroLead} placeholder="Programa, faculty, trabajos, comunicaciones y reportes en una plataforma pensada para equipos médicos." /></label>
            </div>
          </fieldset>
        </div></div>
      </details>

      <details className="panel settings-main settings-full ai-control-panel section-collapse">
        <summary className="section-collapse-summary">
          <span>
            <span className="eyebrow">Inteligencia Artificial</span>
            <strong>Habilitación, crédito y consumo</strong>
            <small>
              Los administradores del evento pueden administrar funciones y
              topes. Las credenciales sólo son accesibles para el
              superadministrador.
            </small>
          </span>
          <span className="section-collapse-chevron" aria-hidden="true">⌄</span>
        </summary>
        <div className="section-collapse-content">
        {superAdmin ? <AiRoutingSettings eventId={event.id}
          openAiCredentials={openAiCredentials} openAiBilling={openAiBilling}
          openAiConfigured={credentialStatus ? credentialStatus.apiKeySource !== "missing" : Boolean(event.aiApiKeyConfigured)}
          modulesEnabled={aiModulesEnabled}
          moduleControls={{organize: (<AiScopeControl code="Organize"
            title="IA habilitada para Organize"
            help="Perfiles, traducciones, programas y corrección administrativa."
            enabled={aiModulesEnabled.organize}
            onEnabledChange={(enabled) => setAiModulesEnabled((current) => ({ ...current, organize: Number(enabled) }))}
            mode={event.aiOrganizeMode}
            limit={event.aiOrganizeLimitUsd}
            spent={event.aiOrganizeSpentUsd}
          />),
abstracts: (<AiScopeControl code="Jury"
            title="IA habilitada para Call4Science"
            help="Optimización, validación del reglamento y evaluación de trabajos científicos."
            enabled={aiModulesEnabled.abstracts}
            onEnabledChange={(enabled) => setAiModulesEnabled((current) => ({ ...current, abstracts: Number(enabled) }))}
            mode={event.aiJuryMode}
            limit={event.aiJuryLimitUsd}
            spent={event.aiJurySpentUsd}
          />),
meetingplanner: (<AiScopeControl code="MeetingPlanner"
            title="IA habilitada para MeetingPlanner"
            help="Portal público, autores, faculty, aplicación móvil y concierge."
            enabled={aiModulesEnabled.meetingplanner}
            onEnabledChange={(enabled) => setAiModulesEnabled((current) => ({ ...current, meetingplanner: Number(enabled) }))}
            mode={event.aiMeetingPlannerMode}
            limit={event.aiMeetingPlannerLimitUsd}
            spent={event.aiMeetingPlannerSpentUsd}
          />)}}
        /> : <>{openAiBilling}<div className="ai-scopes-grid">          <AiScopeControl
            code="Organize"
            title="IA habilitada para Organize"
            help="Perfiles, traducciones, programas y corrección administrativa."
            enabled={aiModulesEnabled.organize}
            onEnabledChange={(enabled) => setAiModulesEnabled((current) => ({ ...current, organize: Number(enabled) }))}
            mode={event.aiOrganizeMode}
            limit={event.aiOrganizeLimitUsd}
            spent={event.aiOrganizeSpentUsd}
          />
          <AiScopeControl
            code="Jury"
            title="IA habilitada para Call4Science"
            help="Optimización, validación del reglamento y evaluación de trabajos científicos."
            enabled={aiModulesEnabled.abstracts}
            onEnabledChange={(enabled) => setAiModulesEnabled((current) => ({ ...current, abstracts: Number(enabled) }))}
            mode={event.aiJuryMode}
            limit={event.aiJuryLimitUsd}
            spent={event.aiJurySpentUsd}
          />
          <AiScopeControl
            code="MeetingPlanner"
            title="IA habilitada para MeetingPlanner"
            help="Portal público, autores, faculty, aplicación móvil y concierge."
            enabled={aiModulesEnabled.meetingplanner}
            onEnabledChange={(enabled) => setAiModulesEnabled((current) => ({ ...current, meetingplanner: Number(enabled) }))}
            mode={event.aiMeetingPlannerMode}
            limit={event.aiMeetingPlannerLimitUsd}
            spent={event.aiMeetingPlannerSpentUsd}
          />
</div></>}
        <p className="field-help">
          El consumo es estimado a partir de tokens y las tarifas técnicas
          definidas en .env; no reemplaza la facturación del proveedor.
        </p>
        </div>
      </details>

      {Boolean(event.abstractsEnabled) && <details className="panel settings-main settings-full section-collapse">
        <summary className="section-collapse-summary">
          <span>
            <span className="eyebrow">Call for Science</span>
            <strong>Recepción y arbitraje de trabajos</strong>
            <small>
              Programá el cierre, la asignación automática y la apertura del
              portal de arbitraje.
            </small>
          </span>
          <span className="section-collapse-chevron" aria-hidden="true">⌄</span>
        </summary>
        <div className="section-collapse-content">
        <div className="form-grid settings-form-grid">
          <SubmissionModalitiesControl event={event} />
          <fieldset className="assignment-field field-wide">
            <legend>Textos del acceso a Call4Science</legend>
            <p>Personalizá los textos que verá el autor al ingresar a Call4Science para este evento.</p>
            <div className="form-grid">
              <label className="field">
                <span>Texto superior</span>
                <input
                  name="callForScienceHeroEyebrow"
                  maxLength={160}
                  defaultValue={event.callForScienceHeroEyebrow}
                  placeholder="Organización científica, simplificada"
                />
              </label>
              <label className="field field-wide">
                <span>Título principal</span>
                <textarea
                  name="callForScienceHeroTitle"
                  rows={3}
                  maxLength={300}
                  defaultValue={event.callForScienceHeroTitle}
                  placeholder="Tu investigación merece una presentación impecable."
                />
              </label>
              <label className="field field-wide">
                <span>Texto descriptivo</span>
                <textarea
                  name="callForScienceHeroLead"
                  rows={3}
                  maxLength={600}
                  defaultValue={event.callForScienceHeroLead}
                  placeholder="Cargá, editá y enviá trabajos científicos desde un único espacio, claro y seguro."
                />
              </label>
            </div>
          </fieldset>
          <label className="field field-wide">
            <span>Mensaje de bienvenida en Abstracts</span>
            <textarea
              name="callForScienceWelcome"
              rows={5}
              defaultValue={event.callForScienceWelcome}
              placeholder="Bienvenido al llamado científico…"
            />
          </label>
          <label className="field field-wide">
            <span>Reglamento del evento</span>
            <textarea
              name="callForScienceRules"
              rows={12}
              defaultValue={event.callForScienceRules}
              placeholder="Bases, formatos, criterios, fechas y condiciones…"
            />
            <small>
              Abstracts lo mostrará en un modal y generará automáticamente su
              versión PDF descargable.
            </small>
          </label>
          <RulesTranslationStatus eventId={event.id} source={event.callForScienceRules || ""}/>
          <label className="field">
            <span>Árbitros humanos por trabajo</span>
            <input
              name="reviewersPerAbstract"
              type="number"
              min="1"
              max="10"
              defaultValue={event.reviewersPerAbstract || 2}
              required
            />
          </label>
          <fieldset className="assignment-field review-criteria-editor field-wide">
            <legend>Ítems de evaluación</legend>
            <p>Estos ítems y sus pesos se aplican únicamente a {event.name}. Cada árbitro los puntuará de 0 a 10; el promedio ponderado será el puntaje general.</p>
            <input type="hidden" name="reviewCriteriaJson" value={JSON.stringify(reviewCriteria)} />
            <div className="review-criteria-list">{reviewCriteria.map((criterion, index) => <div className="review-criterion-row" key={criterion.id}>
              <label className="field"><span>Ítem</span><input value={criterion.name} onChange={(change) => setReviewCriteria((current) => current.map((item, position) => position === index ? { ...item, name: change.target.value } : item))} /></label>
              <label className="field"><span>Peso</span><input type="number" min="0.1" max="10" step="0.1" value={criterion.weight} onChange={(change) => setReviewCriteria((current) => current.map((item, position) => position === index ? { ...item, weight: Number(change.target.value) } : item))} /></label>
              <IconActionButton label={`Eliminar ${criterion.name}`} icon="cancel" tone="danger" disabled={reviewCriteria.length <= 1} onClick={() => { setReviewCriteria((current) => current.filter((_, position) => position !== index)); if (classificationMetric === criterion.id) setClassificationMetric("average"); }} />
            </div>)}</div>
            <IconActionButton label="+ Agregar ítem" icon="add" tone="secondary" type="button" onClick={() => setReviewCriteria((current) => [...current, { id: `criterio-${Date.now()}`, name: `Nuevo ítem ${current.length + 1}`, weight: 1 }])} />
          </fieldset>
          <label className="field field-wide">
            <span>Puntaje utilizado para la clasificación</span>
            <select name="classificationMetric" value={classificationMetric} onChange={(change) => setClassificationMetric(change.target.value)}>
              <option value="average">Promedio general ponderado</option>
              {reviewCriteria.map((criterion) => <option value={criterion.id} key={criterion.id}>{criterion.name}</option>)}
            </select>
            <small>Este valor será el utilizado para ordenar y aplicar los puntos de corte.</small>
          </label>
          <label className="field">
            <span>Máximo de caracteres por abstract</span>
            <input
              name="abstractMaxChars"
              type="number"
              min="100"
              max="100000"
              step="100"
              defaultValue={event.abstractMaxChars || 3000}
              required
            />
            <small>Se cuentan todos los caracteres visibles, incluidos espacios.</small>
          </label>
          <label className="field">
            <span>Máximo de diapositivas por caso clínico</span>
            <input
              name="clinicalCaseMaxPages"
              type="number"
              min="1"
              max="500"
              defaultValue={event.clinicalCaseMaxPages || 20}
              required
            />
            <small>Se valida automáticamente al cargar un archivo PPTX.</small>
          </label>
          {event.aiJuryAvailable && (
            <label className="check-field reviewer-ai-setting">
              <input
                name="aiReviewerEnabled"
                value="1"
                type="checkbox"
                defaultChecked={Boolean(event.aiReviewerEnabled)}
              />
              <span>
                <strong>Agregar un árbitro por IA</strong>
                <small>
                  Será una evaluación adicional y no reemplazará la decisión del
                  comité.
                </small>
              </span>
            </label>
          )}
          <label className="field">
            <span>Apertura de recepción de trabajos</span>
            <input
              name="abstractSubmissionOpensAt"
              type="datetime-local"
              defaultValue={dateTimeLocal(event.abstractSubmissionOpensAt)}
            />
          </label>
          <label className="field">
            <span>Cierre de recepción de trabajos</span>
            <input
              name="abstractSubmissionClosesAt"
              type="datetime-local"
              defaultValue={dateTimeLocal(event.abstractSubmissionClosesAt)}
            />
          </label>
          <label className="field">
            <span>Asignación automática de árbitros</span>
            <input
              name="reviewerAssignmentAt"
              type="datetime-local"
              defaultValue={dateTimeLocal(event.reviewerAssignmentAt)}
            />
          </label>
          <label className="field">
            <span>Comienzo de los arbitrajes</span>
            <input
              name="reviewStartsAt"
              type="datetime-local"
              defaultValue={dateTimeLocal(event.reviewStartsAt)}
            />
          </label>
          <label className="field">
            <span>Punto de corte para presentación oral</span>
            <input
              name="oralCutoffScore"
              type="number"
              min="0"
              max="10"
              step="0.1"
              defaultValue={event.oralCutoffScore ?? 8}
            />
          </label>
          <label className="field">
            <span>Cantidad máxima de orales</span>
            <input
              name="oralSelectionLimit"
              type="number"
              min="0"
              defaultValue={event.oralSelectionLimit || 0}
            />
            <small>0 significa sin límite.</small>
          </label>
          <label className="field">
            <span>Punto de corte para póster</span>
            <input
              name="posterCutoffScore"
              type="number"
              min="0"
              max="10"
              step="0.1"
              defaultValue={event.posterCutoffScore ?? 6}
            />
          </label>
          <label className="field">
            <span>Cantidad máxima de pósters</span>
            <input
              name="posterSelectionLimit"
              type="number"
              min="0"
              defaultValue={event.posterSelectionLimit || 0}
            />
            <small>0 significa sin límite.</small>
          </label>
        </div>
        <div className="review-workflow-status">
          <div>
            <strong>Asignación</strong>
            <span>
              {event.reviewerAssignmentsRunAt
                ? `Ejecutada ${formatDateTime(event.reviewerAssignmentsRunAt)}`
                : "Pendiente"}
            </span>
          </div>
          <div>
            <strong>Notificaciones</strong>
            <span>
              {event.reviewNotificationsSentAt
                ? `Enviadas ${formatDateTime(event.reviewNotificationsSentAt)}`
                : "Pendientes"}
            </span>
          </div>
          <div className="review-workflow-actions">
            <IconActionButton
              label="Asignar árbitros ahora"
              icon="users"
              busy={reviewWorkflowBusy}
              disabled={reviewWorkflowBusy}
              onClick={() => runReviewWorkflow("assign")}
            />
            <IconActionButton
              label="Iniciar arbitraje y notificar"
              icon="send"
              tone="primary"
              busy={reviewWorkflowBusy}
              disabled={reviewWorkflowBusy}
              onClick={() => runReviewWorkflow("start")}
            />
          </div>
        </div>
        {reviewWorkflowMessage && (
          <div className="inline-notice review-workflow-message">
            {reviewWorkflowMessage}
          </div>
        )}
        <p className="field-help">
          La asignación cruza la unidad temática del trabajo con las temáticas
          habilitadas para cada árbitro y distribuye la carga. Guardá los
          cambios antes de ejecutar manualmente.
        </p>
        <div className="cfs-template-admin">
          <div>
            <span className="eyebrow">Material para autores</span>
            <h3>Plantillas de presentación</h3>
            <p>Podés publicar varias bases por evento y por tipo de trabajo.</p>
          </div>
          <div className="template-upload-grid">
            <label className="field">
              <span>Nombre visible</span>
              <input
                value={templateName}
                onChange={(change) => setTemplateName(change.target.value)}
                placeholder="Ej. ePoster 16:9 claro"
              />
            </label>
            <label className="field">
              <span>Aplicar a</span>
              <select
                value={templateWorkType}
                onChange={(change) => setTemplateWorkType(change.target.value)}
              >
                <option value="all">Todos los trabajos</option>
                <option value="abstract">Abstract</option>
                <option value="clinical_case">Caso clínico</option>
                <option value="poster">ePoster</option>
              </select>
            </label>
            <label className={`action-icon-button secondary template-file-button ${templateBusy ? "busy" : ""}`} title={templateBusy ? "Subiendo plantilla…" : "Subir plantilla"} aria-label={templateBusy ? "Subiendo plantilla" : "Subir plantilla"}>
              <ActionIcon name={templateBusy ? "refresh" : "upload"} />
              <span className="visually-hidden">{templateBusy ? "Subiendo plantilla" : "Subir plantilla"}</span>
              <input
                type="file"
                disabled={templateBusy}
                accept=".doc,.docx,.ppt,.pptx,.pdf"
                onChange={(change) => {
                  void uploadTemplate(change.target.files?.[0]);
                  change.target.value = "";
                }}
              />
            </label>
          </div>
          <div className="cfs-template-list">
            {templates.length ? (
              templates.map((template) => (
                <article key={template.id}>
                  <div>
                    <strong>{template.name}</strong>
                    <small>
                      {template.fileName} · {formatBytes(template.size)} ·{" "}
                      {workTypeLabel(template.workType)}
                    </small>
                  </div>
                  <IconActionLink label={`Descargar ${template.name}`} icon="download" href={template.downloadUrl} />
                  <AdminActionButton tone="delete" label={`Eliminar plantilla ${template.name}`} onClick={() => void deleteTemplate(template.id)}><DeleteIcon /></AdminActionButton>
                </article>
              ))
            ) : (
              <p className="field-help">
                Todavía no hay plantillas publicadas.
              </p>
            )}
          </div>
        </div>
        </div>
      </details>}

      <section className="panel settings-main settings-full">
        <div className="panel-heading compact">
          <div>
            <span className="eyebrow">Apariencia</span>
            <h2>Tamaño de tipografía</h2>
            <p>
              Ajustá el texto de todo el sitio y del portal de faculty para este
              evento.
            </p>
          </div>
          <output className="font-scale-value" htmlFor="font-scale">
            {fontScalePercent}%
          </output>
        </div>
        <div className="font-scale-control">
          <span aria-hidden="true">A</span>
          <input
            id="font-scale"
            name="fontScalePercent"
            type="range"
            min="85"
            max="140"
            step="5"
            value={fontScalePercent}
            onChange={(change) =>
              setFontScalePercent(Number(change.target.value))
            }
            aria-label="Tamaño de tipografía del sitio"
          />
          <strong aria-hidden="true">A</strong>
        </div>
        <div className="font-scale-labels">
          <span>Más pequeña</span>
          <IconActionButton label="Restablecer tipografía a 100%" icon="refresh" onClick={() => setFontScalePercent(100)} />
          <span>Más grande</span>
        </div>
      </section>

      <section className="panel settings-main settings-full">
        <div className="panel-heading compact">
          <div>
            <span className="eyebrow">Traducción simultánea</span>
            <h2>Acceso a traducción simultánea</h2>
            <p>Configurá quién puede acceder al audio de traducción durante las sesiones.</p>
          </div>
        </div>
        <label className="field">
          <span>Quién puede escuchar</span>
          <select
            name="translationAccess"
            defaultValue={event.translationAccess || "everyone"}
            key={`translation-${event.id}-${event.translationAccess}`}
          >
            <option value="everyone">Cualquiera</option>
            <option value="room">Sólo personas detectadas por balizas en la sala de la sesión</option>
          </select>
          <small>
            La opción por sala requiere acceso validado en la app y una baliza activa asignada a esa sala.
            La escucha se interrumpe si no se renueva la detección durante 45 segundos.
          </small>
        </label>
      </section>

      <section className="panel database-shadow-status">
        <span
          className={`database-status-dot ${sqlServer?.status || "disabled"}`}
        />
        <div>
          <span className="eyebrow">Resguardo de datos</span>
          <h2>Espejo en SQL Server</h2>
          <p>
            {!sqlServer?.enabled
              ? "Preparado, todavía sin activar."
              : sqlServer.status === "ready"
                ? "Sincronización automática funcionando."
                : sqlServer.status === "degraded"
                  ? "La aplicación sigue operativa; SQL Server tiene reintentos pendientes."
                  : "Conectando con SQL Server…"}
          </p>
        </div>
        <div className="database-shadow-metrics">
          <strong>{sqlServer?.pending ?? 0}</strong>
          <span>pendientes</span>
          <strong>{sqlServer?.processed ?? 0}</strong>
          <span>replicados</span>
        </div>
        {sqlServer?.lastError && (
          <small title={sqlServer.lastError}>
            Último aviso: {sqlServer.lastError}
          </small>
        )}
      </section>

      <QuizCompetitionSettings key={event.id} eventId={event.id} exhibitors={catalogs.exhibitors}/>

      <section className="master-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Estructura del programa</span>
            <h2>Tablas maestras</h2>
            <p>
              Administrá los valores disponibles al crear sesiones y
              asignaciones.
            </p>
          </div>
        </div>
        <div className="master-grid">
          {masters.map((master) => (
            <MasterTable
              {...master}
              items={catalogs[master.resource] || []}
              onAdd={() => onAdd(master.resource)}
              onEdit={(item) => onEdit(master.resource, item)}
              onDelete={(item) => onDelete(master.resource, item)}
              key={master.resource}
            />
          ))}
        </div>
      </section>
      <FloorPlanManager onChanged={onEventRefresh} revision={JSON.stringify(catalogs.beacons)} eventId={event.id} exhibitors={catalogs.exhibitors} rooms={catalogs.rooms} initialPositioningMode={event.positioningMode || "general"} />
      {openAiUsage && (
        <Modal
          title="Consumo de OpenAI"
          eyebrow="Facturación oficial de la organización"
          wide
          className="openai-usage-modal"
          onClose={() => setOpenAiUsage(null)}
        >
          <div className="openai-usage-report">
            <div
              className={`openai-usage-totals ${openAiUsage.balance !== null ? "has-balance" : ""}`}
            >
              <article>
                <span>Últimos 7 días</span>
                <strong>
                  {formatUsd(
                    openAiUsage.totals.last7Days,
                    openAiUsage.currency,
                  )}
                </strong>
              </article>
              <article>
                <span>Últimos 30 días</span>
                <strong>
                  {formatUsd(
                    openAiUsage.totals.last30Days,
                    openAiUsage.currency,
                  )}
                </strong>
              </article>
              <article>
                <span>Mes actual</span>
                <strong>
                  {formatUsd(
                    openAiUsage.totals.currentMonth,
                    openAiUsage.currency,
                  )}
                </strong>
              </article>
              {openAiUsage.balance !== null && (
                <article className="openai-balance-total">
                  <span>Saldo disponible</span>
                  <strong>
                    {formatUsd(
                      openAiUsage.balance,
                      openAiUsage.spendLimit?.currency || openAiUsage.currency,
                    )}
                  </strong>
                </article>
              )}
            </div>
            {openAiUsage.spendLimit && (
              <div className="openai-balance-notice">
                <strong>Saldo del límite del proyecto</strong>
                <p>{openAiUsage.balanceMessage}</p>
                <small>
                  {openAiUsage.spendLimit.enforcement === "hard"
                    ? "Límite duro"
                    : `Aplicación: ${openAiUsage.spendLimit.enforcement}`}{" "}
                  · Período:{" "}
                  {openAiUsage.spendLimit.interval === "month"
                    ? "mensual"
                    : openAiUsage.spendLimit.interval}
                </small>
              </div>
            )}
            <section>
              <h3>Proyectos de la organización</h3>
              <div className="responsive-table">
                <table>
                  <thead>
                    <tr>
                      <th>Proyecto</th>
                      <th>Estado</th>
                      <th>Últimos 30 días</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openAiUsage.projects.map((project) => (
                      <tr key={project.id}>
                        <td>
                          <strong>{project.name}</strong>
                          <small className="table-secondary-line">
                            {project.id}
                          </small>
                        </td>
                        <td>
                          {project.status === "active" ? "Activo" : "Archivado"}
                        </td>
                        <td>
                          <strong>
                            {formatUsd(project.amount, openAiUsage.currency)}
                          </strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section>
              <h3>Detalle de consumo de los últimos 30 días</h3>
              {openAiUsage.details.length ? (
                <div className="responsive-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Rubro</th>
                        <th>Proyecto</th>
                        <th>Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openAiUsage.details.map((item) => (
                        <tr key={`${item.lineItem}-${item.projectId}`}>
                          <td>{item.lineItem}</td>
                          <td>
                            <strong>{item.projectName}</strong>
                            <small className="table-secondary-line">
                              {item.projectId}
                            </small>
                          </td>
                          <td>
                            <strong>
                              {formatUsd(item.amount, openAiUsage.currency)}
                            </strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="field-help">
                  OpenAI no informó consumos para este período.
                </p>
              )}
            </section>
            <section className="openai-transaction-audit">
              <div className="openai-transaction-heading">
                <div>
                  <h3>Transacciones realizadas por Meeting Organize</h3>
                  <p>
                    Operaciones registradas localmente durante los últimos{" "}
                    {openAiUsage.localUsage.periodDays} días.
                  </p>
                </div>
                <strong>
                  {formatTokens(openAiUsage.localUsage.totalTokens)} tokens
                </strong>
              </div>
              <div className="openai-token-totals">
                <article>
                  <span>Transacciones</span>
                  <strong>
                    {formatTokens(openAiUsage.localUsage.transactionCount)}
                  </strong>
                </article>
                <article>
                  <span>Tokens de entrada</span>
                  <strong>
                    {formatTokens(openAiUsage.localUsage.inputTokens)}
                  </strong>
                </article>
                <article>
                  <span>Tokens de salida</span>
                  <strong>
                    {formatTokens(openAiUsage.localUsage.outputTokens)}
                  </strong>
                </article>
                <article>
                  <span>Costo local estimado</span>
                  <strong>
                    {formatUsd(openAiUsage.localUsage.estimatedUsd, "usd")}
                  </strong>
                </article>
              </div>
              {openAiUsage.localUsage.transactions.length ? (
                <div className="responsive-table openai-transactions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Operación</th>
                        <th>Módulo</th>
                        <th>Modelo</th>
                        <th>Entrada</th>
                        <th>Salida</th>
                        <th>Total</th>
                        <th>Costo estimado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openAiUsage.localUsage.transactions.map(
                        (transaction) => (
                          <tr key={transaction.id}>
                            <td>{formatDateTime(transaction.createdAt)}</td>
                            <td>
                              <strong>{transaction.description}</strong>
                              <small className="table-secondary-line">
                                {transaction.feature}
                              </small>
                            </td>
                            <td>{aiScopeLabel(transaction.scope)}</td>
                            <td>{transaction.model || "Sin informar"}</td>
                            <td>{formatTokens(transaction.inputTokens)}</td>
                            <td>{formatTokens(transaction.outputTokens)}</td>
                            <td>
                              <strong>
                                {formatTokens(transaction.totalTokens)}
                              </strong>
                            </td>
                            <td>
                              {formatUsd(transaction.estimatedUsd, "usd")}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="field-help">
                  Todavía no hay transacciones de IA registradas localmente para
                  este evento.
                </p>
              )}
              {openAiUsage.localUsage.transactionCount >
                openAiUsage.localUsage.shownTransactions && (
                <small className="openai-transaction-limit">
                  Se muestran las{" "}
                  {formatTokens(openAiUsage.localUsage.shownTransactions)} más
                  recientes de{" "}
                  {formatTokens(openAiUsage.localUsage.transactionCount)}{" "}
                  transacciones.
                </small>
              )}
            </section>
            <small>Consultado: {formatDateTime(openAiUsage.queriedAt)}</small>
          </div>
          <div className="modal-footer">
            <IconActionButton
              label="Cerrar consumo de OpenAI"
              icon="close"
              onClick={() => setOpenAiUsage(null)}
            />
            <IconActionButton
              label="Actualizar consumo de OpenAI"
              icon="refresh"
              tone="primary"
              busy={openAiUsageBusy}
              disabled={openAiUsageBusy}
              onClick={() => void queryOpenAiUsage()}
            />
          </div>
        </Modal>
      )}
    </form>
  );
}

function MasterTable({
  resource,
  code,
  title,
  description,
  items,
  onAdd,
  onEdit,
  onDelete,
}: {
  resource: CatalogResource;
  code: string;
  title: string;
  description: string;
  items: CatalogItem[];
  onAdd: () => void;
  onEdit: (item: CatalogItem) => void;
  onDelete: (item: CatalogItem) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <article className={`panel master-card ${open ? "open" : "collapsed"}`}>
      <header onClick={() => setOpen((current) => !current)}>
        <span className="structure-code">{code}</span>
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <button
          className="master-collapse"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setOpen((current) => !current);
          }}
          aria-expanded={open}
          aria-label={`${open ? "Cerrar" : "Abrir"} ${title}`}
        >
          ⌄
        </button>
        <button
          className="master-add"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onAdd();
          }}
          aria-label={`Agregar en ${title}`}
        >
          +
        </button>
      </header>
      {open && (
        <>
          <div className="master-list">
            {items.length === 0 && (
              <div className="master-empty">Todavía no hay registros.</div>
            )}
            {items.map((item) => (
              <div className="master-row" key={item.id}>
                <span
                  className="master-color"
                  style={{ background: itemColor(resource, item) }}
                />
                <div>
                  <strong>{itemLabel(resource, item)}</strong>
                  <small>{itemDetail(resource, item)}</small>
                </div>
                <div className="master-actions">
                  <AdminActionButton tone="edit" label={`Editar ${itemLabel(resource, item)}`} onClick={() => onEdit(item)}><EditIcon /></AdminActionButton>
                  <AdminActionButton tone="delete" label={`Eliminar ${itemLabel(resource, item)}`} onClick={() => onDelete(item)}><DeleteIcon /></AdminActionButton>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </article>
  );
}

function itemLabel(resource: CatalogResource, item: CatalogItem) {
  if (resource === "exhibitors" && "standCode" in item)
    return `${item.name}${item.standCode ? ` · Stand ${item.standCode}` : ""}`;
  return "name" in item ? item.name : "Registro";
}

function itemDetail(resource: CatalogResource, item: CatalogItem) {
  if (resource === "days" && "date" in item)
    return `${formatDate(item.date)} · Orden ${item.sortOrder}`;
  if (resource === "blocks" && "startTime" in item)
    return `${item.dayName} · ${item.startTime}–${item.endTime} · Orden ${item.sortOrder}`;
  if (resource === "rooms" && "streamingCode" in item)
    return `${item.streamingCode || "Sin código"} · Orden ${item.sortOrder}`;
  if (resource === "topics" && "application" in item)
    return item.application === "abstracts"
      ? "Trabajos científicos"
      : item.application === "both"
        ? "Programa y trabajos científicos"
        : "Programa";
  if (resource === "modes" && "prefix" in item)
    return item.prefix || "Sin prefijo";
  if (resource === "roles" && "participationMinutes" in item)
    return `${item.participationMinutes} min · ${item.fullAccess ? "Acceso completo" : "Acceso estándar"}`;
  if (resource === "exhibitors" && "contactEmail" in item)
    return item.contactEmail || "Sin contacto";
  if (resource === "facultyTypes" && "description" in item && "active" in item)
    return `${item.description || "Sin descripción"} · Orden ${item.sortOrder} · ${item.active ? "Activo" : "Inactivo"}`;
  if (
    resource === "eventStatuses" &&
    "description" in item &&
    "color" in item &&
    "active" in item
  )
    return `${item.scope === "abstracts" ? "Abstracts" : "Organize"} · ${item.description || "Sin descripción"} · Orden ${item.sortOrder} · ${item.active ? "Activo" : "Inactivo"}`;
  if (resource === "scientificTables" && "type" in item && "active" in item)
    return `${item.type === "oral" ? "Presentaciones orales" : "Sesión de pósters"}${item.topicName ? ` · ${item.topicName}` : ""} · ${item.presentationMinutes} min de presentación + ${item.discussionMinutes} min de discusión · ${item.active ? "Activa" : "Inactiva"}`;
  return "";
}

function itemColor(resource: CatalogResource, item: CatalogItem) {
  if (
    (resource === "topics" ||
      resource === "modes" ||
      resource === "eventStatuses") &&
    "color" in item
  )
    return item.color;
  return "#c8d8df";
}

function singular(resource: CatalogResource) {
  return (
    {
      days: "día",
      blocks: "bloque horario",
      rooms: "sala",
      topics: "temática",
      modes: "modalidad",
      roles: "rol",
      exhibitors: "expositor",
      facultyTypes: "tipo de Faculty",
      eventStatuses: "estado del evento",
      scientificTables: "mesa científica",
    } as Record<CatalogResource, string>
  )[resource];
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(value));
}

function dateTimeLocal(value: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatUsd(value: number, currency = "usd") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(Number(value || 0));
}

function formatTokens(value: number) {
  return new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(
    Number(value || 0),
  );
}

function aiScopeLabel(value: string) {
  return (
    {
      organize: "Organize",
      jury: "Jurado IA",
      meetingplanner: "Meeting Planner",
    } as Record<string, string>
  )[value] || value;
}

function EventAssetField({
  name,
  removeName,
  value,
  title,
  help,
  wide = false,
}: {
  name: string;
  removeName: string;
  value: string;
  title: string;
  help: string;
  wide?: boolean;
}) {
  const [preview, setPreview] = useState(value || "");
  return (
    <div className={`settings-event-asset ${wide ? "wide" : ""}`}>
      <div className="settings-event-asset-preview">
        {preview ? <img src={preview} alt={title} /> : <span>Sin imagen</span>}
      </div>
      <div>
        <strong>{title}</strong>
        <small>{help}</small>
        <label className="action-icon-button secondary" title={`Seleccionar ${title}`} aria-label={`Seleccionar ${title}`}>
          <ActionIcon name="upload" />
          <span className="visually-hidden">Seleccionar {title}</span>
          <input
            name={name}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(change) => {
              const file = change.target.files?.[0];
              if (file) setPreview(URL.createObjectURL(file));
            }}
          />
        </label>
        {value && (
          <label className="photo-remove">
            <input
              name={removeName}
              type="checkbox"
              value="1"
              onChange={(change) =>
                setPreview(change.target.checked ? "" : value)
              }
            />{" "}
            Quitar imagen actual
          </label>
        )}
      </div>
    </div>
  );
}

function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
}
function formatBytes(bytes: number) {
  return bytes < 1_000_000
    ? `${Math.ceil(bytes / 1000)} KB`
    : `${(bytes / 1_000_000).toFixed(1)} MB`;
}
function workTypeLabel(value: string) {
  return (
    (
      {
        all: "Todos",
        abstract: "Abstract",
        clinical_case: "Caso clínico",
        poster: "ePoster",
      } as Record<string, string>
    )[value] || value
  );
}
